/*
 * Handles all communication with the Electron main process
 *
 */

import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { useSettingsStore } from '@/stores/Settings'
import { useRealtimeStore } from '@/stores/Realtime'
import { useEmployeesStore } from '@/stores/Employees'
import { useApiStore } from '@/stores/Api'
import { useI18n } from 'vue-i18n'

import { cloneObject, sleep } from '@/utilities/Tools'
import globalEvents from '@/utilities/GlobalEvents'
import logger from '@/utilities/Logger'
import SipClient from '@/utilities/SipClient'
import axios, { type CancelTokenSource } from 'axios'

import { IntegrationGetCommand, type ZerotierResponse, type IntegrationBrowserData, type IntegrationGetResponse, type IntegrationSendKeysData, type WindowElectronAPI, type NotificationShowResponse, BeaconEvent, type Beacon } from '@/stores/Electron.types'
import type { SipClientTcxState } from '@/utilities/SipClient.types'
import { PbxEventType, type ConfigEventBeacon, type PbxEventCall } from '@per_moeller/tcx-realtime-client'
import { PbxAppSettingsIntegrationRunWhen, type PbxAppEmployeesExtensionCallWithDate, type PbxAppSettingsDesktopIntegrationBrowser, type PbxAppSettingsDesktopIntegrationCopyToClipboard, type PbxAppSettingsDesktopIntegrationRun, type PbxAppSettingsDesktopIntegrationSendkeys, type PbxAppSettingsDesktopIntegrationWebsite } from '@/TcxApi/PbxApp'
import { PbxCallState } from '@/TcxApi/PbxCommon'
import { type PbxExtensionDndResponse } from '@/TcxApi/PbxExtension'
import kuando from '@/utilities/headsets/Kuando'

export const useElectronStore = defineStore('electron', () => {
	logger.debug('Electron: Initializing store')

	const ss = useSettingsStore()
	const realtime = useRealtimeStore()
	const api = useApiStore()
	const employees = useEmployeesStore()
	const started = new Date()
	const http = axios.create()
	const sipClient = SipClient.instance

	http.defaults.timeout = 10000
	http.defaults.baseURL = ss.runtime.localUrl

	const enabled = computed(() => { return ss.runtime.isEmbedded })
	const useIPC = computed(() => { return ss.runtime.isEmbeddedIpc })
	const ipc = (window as unknown as WindowElectronAPI).electronAPI
	let pollLocalApiToken: CancelTokenSource | undefined = undefined
	const pollApiConnected = ref(false) // used by old style polling
	const connected = computed(() => { return enabled.value && (useIPC.value ? true : pollApiConnected.value) })
	const currentBeacon = ref<Beacon | undefined>(undefined)
	const lastBeaconEvent = ref<Date>(new Date())

	/**
	 * Used by for status panel to show if beacons are working
	 */
	const isReceivingBeaconEvents = computed(() => {
		if (lastBeaconEvent.value.getTime() + 10000 < new Date().getTime()) {
			return false
		}
		return true
	})

	// #region Public functions

	/**
	 * Set the window always on top
	 * @param alwaysOnTop True to set the window always on top, false to remove it
	 * @returns True if the operation was successful, false if it failed
	 */
	async function setAlwaysOnTop(alwaysOnTop: boolean) {
		if (enabled.value) {
			if (useIPC.value) {
				ipc.setAlwaysOnTop(alwaysOnTop)
				return true
			} else {
				try {
					await http.get(`/set?alwaysOnTop=${alwaysOnTop}`)
					return true
				}
				catch (_) { return false }
			}
		} else {
			return false
		}
	}

	/**
	 * Set the zoom level of the window, 10-200%
	 * @param zoom The zoom level to set, in percent
	 * @returns True if the operation was successful, false if it failed
	 */
	async function setZoom(zoom: number) {
		if (enabled.value) {
			if (useIPC.value) {
				ipc.setZoom(zoom)
				return true
			} else {
				try {
					await http.get(`/set?zoom=${zoom}`)
					return true
				}
				catch (_) { return false }
			}
		} else {
			return false
		}
	}

	/**
	 * Ping the Electron app to check if it is alive and responding - also returns the version of the app
	 * @returns Version of the Electron app
	 * @throws Errors if electron app does not respond
	 * TODO: I dont think this is ever used - consider removing it
	 */
	async function ping() {
		if (enabled.value) {
			if (useIPC.value) {
				return ipc.ping()
			} else {
				const res = await http.get('/ping')

				return res.data.version as string
			}
		} else {
			return ''
		}
	}

	/**
	 * Set the name of the app
	 * @param name The name of the app
	 * @returns True if the operation was successful, false if it failed
	 */
	async function setAppName(name: string) {
		if (enabled.value) {
			if (useIPC.value) {
				ipc.setAppName(name)
				return true
			} else {
				try {
					await http.get(`/set?appName=${name}`)
					return true
				}
				catch (_) { return false }
			}
		} else {
			return false
		}
	}

	/**
	 * Set the state of the beacon scanner
	 * @param enable True to enable the scanner, false to disable it
	 * @param uuid UUID filter - only devices with this UUID will be scanned
	 * @returns True if the operation was successful, false if it failed
	 */
	async function setBeaconScanner(enable: boolean, uuid?: string) {
		if (enabled.value) {
			if (useIPC.value) {
				ipc.setBeaconScanner(enable, uuid)
				return true
			}
			// Not implemented for old style api as it did not support it
		} else {
			return false
		}
	}

	/**
	 * Hide the app window
	 * @returns True if the operation was successful, false if it failed
	 */
	async function hide() {
		if (enabled.value) {
			if (useIPC.value) {
				ipc.hide()
				return true
			} else {
				try {
					await http.get('/hide')
					return true
				}
				catch (_) { return false }
			}
		} else {
			return false
		}
	}

	/**
	 * Show the app window
	 * @returns True if the operation was successful, false if it failed
	 * TODO: This is not used anywhere - consider removing it
	 */
	async function show() {
		if (enabled.value) {
			if (useIPC.value) {
				ipc.show()
				return true
			} else {
				try {
					await http.get('/show')
					return true
				}
				catch (_) { return false }
			}
		} else {
			return false
		}
	}

	/**
	 * Set the window fullscreen or not
	 * @param fullscreen True to set the window fullscreen, false to remove it
	 */
	async function setFullscreen(fullscreen: boolean) {
		if (enabled.value) {
			if (useIPC.value) {
				ipc.setFullscreen(fullscreen)
				return true
			} else {
				try {
					await http.get(`/set?fullscreen=${fullscreen}`)
					return true
				}
				catch (_) { return false }
			}
		}
	}

	/**
	 * Set global shortcuts
	 * @param shortcuts Global shortcuts to register - key: shortcut, value: action
	 */
	async function setGlobalShortcuts(shortcuts: Record<string, string>) {
		if (enabled.value) {
			if (useIPC.value) {
				return ipc.setGlobalShortcuts(shortcuts)
			} else {
				try {
					const sc = Object.entries(shortcuts).map(([key, value]) => { return `${key}=${value}` }).join('&')
					const res = await http.get<{ success: boolean }>('/globalshortcuts?' + sc)

					return res.data.success
				}
				catch (_) { return false }
			}
		}
	}

	/**
	 * Set if the app shouls autolaunch on login or not
	 * @param enabled True to enable autolaunch, false to disable it
	 */
	async function setAutolaunch(enable: boolean) {
		if (enabled.value) {
			if (useIPC.value) {
				return ipc.setAutoLaunch(enable)
			} else {
				try {
					const res = await http.get<{ success: boolean }>(`/autolaunch/${enable ? 'on' : 'off'}`)

					if (!res.data.success) {
						const { t } = useI18n()

						globalEvents.emit('CommonErrorModal', { header: t('integration.errorHeader'), message: t('integration.errorAutolaunchMessage') })
					}
					return res.data.success
				}
				catch (_) { return false }
			}
		} else {
			return false
		}
	}

	async function setWindowBounds(x: number, y: number, width: number, height: number) {
		if (enabled.value) {
			if (useIPC.value) {
				ipc.setWindowBounds(x, y, width, height)
			}
			// Not implemented for old style api as it did not support it
		}
	}

	/**
	 * Download a file
	 * @param url URL to download
	 * @param filename Filename to save as
	 * @param showFolderPicker True if user should be allowd to pick a folder to download to
	 * @returns True on success, false on failure
	 */
	async function download(url: string, filename: string, showFolderPicker: boolean) {
		if (enabled.value) {
			if (useIPC.value) {
				return ipc.download(url, filename, showFolderPicker)
			} else {
				try {
					await http.get(`/download?url=${encodeURIComponent(url)}`)
					return true
				}
				catch (_) { return false }
			}
		}
	}

	/**
	 * Open an external URL in the default browser or whatever app the url is associated with
	 * @param url URL to open
	 */
	async function openExternal(url: string) {
		if (enabled.value) {
			if (useIPC.value) {
				ipc.openExternal(url)
			} else {
				try {
					await http.get(`/openexternal?url=${encodeURIComponent(url)}`)
				}
				catch (_) { /** Ignore */ }
			}
		}
	}

	/**
	 * Terminate the app
	 */
	async function terminate() {
		if (enabled.value) {
			if (useIPC.value) {
				ipc.terminate()
			} else {
				try {
					await http.get('/terminate')
				}
				catch (_) { /** Ignore */ }
			}
		}
	}

	/**
	 * Send a command to ZeroTier
	 * @param url ZeroTier URL to invoke
	 * @param method Method to use
	 * @param body Body payload if method is POST
	 * @returns Response object with success, status, message and data
	 */
	async function zerotier(url: string, method: 'GET' | 'POST' | 'DELETE', body?: string) {
		if (enabled.value) {
			if (useIPC.value) {
				return ipc.zerotier(url, method, body)
			} else {
				try {
					const res = await http.get<ZerotierResponse>(`/zerotier?url=${encodeURIComponent(url)}&method=${method}${body ? '&body=' + encodeURIComponent(body) : ''}`)

					return res.data
				}
				catch (err) { return { status: 500, message: (err as Error).message, success: false } as ZerotierResponse }
			}
		} else {
			return { status: 500, message: 'Electron not enabled', success: false } as ZerotierResponse
		}
	}

	/**
	 * Show or hide the devtools window
	 * @param show True to show the dev tools, false to hide them
	 * @returns True on success, false on failure
	 */
	async function devTools(show: boolean) {
		if (enabled.value) {
			if (useIPC.value) {
				ipc.devTools(show)
				return true
			} else {
				try {
					const res = await http.get<{ success: boolean }>(`/devtools${show ? 'show' : 'hide'}`)

					return res.data.success
				}
				catch (_) { return false }
			}
		}
	}

	/**
	 * Show a system notification to the user (Windows only!)
	 * @param title Title of the notification
	 * @param body Body of the notification
	 * @param icon Optional icon to show in the notification
	 * @param tag Tag for recognizing the notification, so that the user may close it
	 * @returns Response object with success, and weather the notification was clicked or closed
	 */
	async function notificationShow(title: string, body: string | undefined, icon: string | undefined, tag: string) {
		if (enabled.value) {
			if (useIPC.value) {
				return ipc.notificationShow(title, body, icon, tag)
			} else {
				try {
					const query = {
						title: title,
						body: body,
						tag: tag,
						icon: icon
					}
					const res = await http.get<NotificationShowResponse>('/notification/show', { params: query, timeout: 61000 })

					return res.data
				}
				catch (_) { return { success: false, clicked: false, closed: false } as NotificationShowResponse }
			}
		}
	}

	/**
	 * Close a system notification (Windows only!)
	 * @param tag Tag of the notification to close
	 */
	async function notificationClose(tag: string) {
		if (enabled.value) {
			if (useIPC.value) {
				ipc.notificationClose(tag)
			} else {
				await http.get(`/notification/close?tag=${encodeURIComponent(tag)}`)
			}
		}
	}

	// #endregion

	/**
	 * Poll the local API for requests we need to perform (if not using IPC)
	 * - This is used to handle the old style integration with the Electron app and may be removed in the future
	 */
	async function pollLocalApi() {
		if (!pollApiConnected.value) {
			logger.debug('Electron: Trying to connect to local API (app backend) at ' + ss.runtime.localUrl)
		}

		const timeout = pollApiConnected.value ? 10000 : 500

		if (pollLocalApiToken) {
			pollLocalApiToken.cancel()
		}
		pollLocalApiToken = axios.CancelToken.source()

		try {
			const res = await http.get<IntegrationGetResponse>(`${ss.runtime.localUrl}/get?client=MAIN&timeout=${timeout}`, {
				timeout: pollApiConnected.value ? 12000 : 2500,
				cancelToken: pollLocalApiToken.token
			})

			pollLocalApiToken = undefined
			if (!pollApiConnected.value) {
				pollApiConnected.value = true
				logger.debug('Electron: Local API connected (app backend)')
			}

			if (res.data.length > 0) {
				if (typeof res.data === 'string') {
					logger.error('Electron: Received invalid data from local API - trying to fix')
					let data = res.data as unknown as string

					if (!data.endsWith(']')) {
						data = data.substring(0, data.lastIndexOf('},')) + '}]'
					}

					res.data = JSON.parse(data) as unknown as IntegrationGetResponse
					logger.error('Electron: Received invalid data from local API - fixed!')
				}

				for (const command of res.data) {
					logger.info(`Integration: Handling command ${command.command} with messageId ${command.messageId}`)
					switch (command.command) {
						case IntegrationGetCommand.ANSWER: onAnswer(); break
						case IntegrationGetCommand.HANGUP: onHangup(); break
						case IntegrationGetCommand.SEARCH: onSearch(); break
						case IntegrationGetCommand.CALL: onCall(command.number); break
						case IntegrationGetCommand.FULLSCREEN: {
							ss.settings.fullscreen = command.fullscreen ?? false
							logger.log('Electron: Fullscreen set to ' + ss.settings.fullscreen)
							break
						}
						case IntegrationGetCommand.ERROR:
							globalEvents.emit('CommonErrorModal', { header: command.header as string, message: command.message as string })
							break

						case 'LOGGER':
							switch (command.severity) {
								case 'INFO': logger.info(command.message as string); break
								case 'WARNING': logger.warning(command.message as string); break
								case 'ERROR': logger.error(command.message as string); break
								case 'DEBUG': logger.debug(command.message as string); break
							}
							break
					}
				}
			}
			pollLocalApi()
		}
		catch (err) {
			if (!err || (!(err as Error).message && !(err as Error).stack)) { // Request was cancelled
				logger.debug('Electron: Polling was cancelled')
				return
			}
			logger.debug(`Electron: Local API failed: ${(err as Error).message} - not connected anymore`)
			pollLocalApiToken = undefined
			pollApiConnected.value = false

			setTimeout(() => { pollLocalApi() }, 1000)
		}
	}

	// #region Integration

	/**
	 * Replaces number, name and variables
	 * @param str String to perform replacing on
	 * @param call Call object
	 * @param requireArgs True if all variables must be present, otherwise return null
	 * @param testMode True if we are in test mode and should make up test data
	 * @returns Replaced string or null if required arguments are missing
	 */
	async function replace(str: string, call: PbxAppEmployeesExtensionCallWithDate | undefined, requireArgs: boolean, testMode: boolean) {
		logger.info(`Integration: Replacing variables in "${str}"${testMode ? ' - running in test mode' : ''}`)

		const number = testMode ? '+4512345678' : (call?.caller.secret ? '' : call?.caller.number) // NOSONAR
		const name = testMode ? 'Test Testesen' : (call?.caller.secret ? '' : call?.caller.name) // NOSONAR
		const numberNoPrefix = testMode ? '12345678' : (ss.cutNationalPrefix(number ?? '') ?? '')
		let failed = false

		if (str.includes('{NUMBER}') && !number) { failed = true }
		if (str.includes('{NUMBER-NO-PREFIX}') && !number) { failed = true }

		str = str
			.replace(/\{NUMBER\}/g, number ?? '')
			.replace(/\{NAME\}/g, name ?? '')
			.replace(/\{NUMBER-NO-PREFIX\}/g, numberNoPrefix)

		const vars = str.match(/\{VAR:[^}]+\}/g)

		if (!vars) {
			if (requireArgs && failed) { return null }
			return str
		}

		while (vars.length > 0) {
			const variable = vars.shift()
			if (variable) {
				if (testMode) {
					str = str.replace(variable, `${variable.substring(5).slice(0, -1)}:TEST`)
				} else if (call?.callerChannelId) {
					const value = getVariableValue(call.callerChannelId, variable.substring(5).slice(0, -1))
					if (!value) { failed = true }
					str = str.replace(variable, value)
				}
			}
		}

		if (requireArgs && failed) { return null }
		return str
	}

	/**
	 * Get the value of a call variable (live from the PBX)
	 * @param channelId Channel ID of the caller
	 * @param variable Name of the variable to get the value of
	 * @returns The value of the variable or empty string if not found
	 */
	function getVariableValue(channelId: string, variable: string) {
		return employees.callVariables.get(`${channelId}-${variable}`) ?? ''
	}

	/**
	 * Run Copy-to-clipboard integration
	 * @param call Call object
	 * @param settings Test settings, used when testing integration
	 */
	async function integrationClipboard(call?: PbxAppEmployeesExtensionCallWithDate, settings?: PbxAppSettingsDesktopIntegrationCopyToClipboard) {
		const data = settings ?? ss.settings.integration.copyToClipboard
		const what = await replace(data.what as string, call, data.requireArgs ?? false, !!settings) // replaces {NUMBER}, {NUMBER-NO-PREFIX}, {NAME}, {VAR:cpr} in the command

		if (call) {
			logger.info(`Integration Clipboard: Invoked for call ${call._id} ${call.caller.number} -> ${call.callee.number} - state: ${call.state}`)
		}

		if (what && enabled.value) {
			let success = true

			if (useIPC.value) {
				success = await ipc.integrationClipboard(what)
			} else {
				try { await http.post('/integration/clipboard', { content: what }) }
				catch (_) { success = false }
			}

			if (!success) {
				const { t } = useI18n()

				globalEvents.emit('CommonErrorModal', { header: t('integration.errorHeader'), message: t('integration.errorClipboardMessage') })
			}
		}
	}

	/**
	 * Run Run app integration
	 * @param call Call object
	 * @param settings Test settings, used when testing integration
	 */
	async function integrationRun(call?: PbxAppEmployeesExtensionCallWithDate, settings?: PbxAppSettingsDesktopIntegrationRun) {
		const data = settings ?? ss.settings.integration.run

		if (call) {
			logger.info(`Integration Run: Invoked for call ${call._id} ${call.caller.number} -> ${call.callee.number} - state: ${call.state}`)
		}

		if (data.command && enabled.value) {
			await sleep(data.delay ?? 0)

			const command = await replace(data.command, call, data.requireArgs ?? false, !!settings) // replaces {NUMBER}, {NUMBER-NO-PREFIX}, {NAME}, {VAR:cpr} in the command

			if (command) {
				let success = true

				if (useIPC.value) {
					try {
						success = await ipc.integrationRun(command)
					}
					catch (err) {
						logger.error(`Integration Run: IPC call failed: ${(err as Error).message}`)
						success = false
					}
				} else {
					try { await http.post('/integration/run', { command }) }
					catch (_) { success = false }
				}

				if (!success) {
					const { t } = useI18n()

					globalEvents.emit('CommonErrorModal', { header: t('integration.errorHeader'), message: t('integration.errorRunMessage') })
				}
			}
		}
	}

	/**
	 * Run Website app integration
	 * @param call Call object
	 * @param settings Test settings, used when testing integration
	 */
	async function integrationWebsite(call?: PbxAppEmployeesExtensionCallWithDate, settings?: PbxAppSettingsDesktopIntegrationWebsite) {
		const data = settings ?? ss.settings.integration.website

		if (call) {
			logger.info(`Integration Website/API: Invoked for call ${call._id} ${call.caller.number} -> ${call.callee.number} - state: ${call.state}`)
		}

		await sleep(data.delay ?? 0)
		if (data.url) {
			if (!data.apiMode || (data.apiMode as string) === 'NO') {
				const url = await replace(data.url, call, data.requireArgs ?? false, !!settings) // replaces {NUMBER}, {NUMBER-NO-PREFIX}, {NAME}, {VAR:cpr} in the command
				if (url) {
					if (data.preferWindow) {
						window.open(url, '_blank', 'noopener,noreferrer,toolbar=yes,location=yes,menubar=yes,scrollbars=yes,resizable=yes')
					} else {
						window.open(url, '_blank')
					}
				}
			} else if (data.apiMode === 'GET') {
				const url = await replace(data.url, call, data.requireArgs ?? false, !!settings) // replaces {NUMBER}, {NUMBER-NO-PREFIX}, {NAME}, {VAR:cpr} in the command
				if (url) {
					try {
						await axios.get(url, { timeout: 10000 })
					}
					catch (err) {
						logger.error(`Integration Website/API: GET ${url} failed: ${(err as Error).message}`)
					}
				}
			} else { // POST, PATCH, PUT
				const url = await replace(data.url, call, data.requireArgs ?? false, !!settings) // replaces {NUMBER}, {NUMBER-NO-PREFIX}, {NAME}, {VAR:cpr} in the command
				const body = await replace(data.apiData ?? '', call, data.requireArgs ?? false, !!settings) // replaces {NUMBER}, {NUMBER-NO-PREFIX}, {NAME}, {VAR:cpr} in the command
				if (url && body) {
					try {
						await axios.post(url, body, { timeout: 10000 })
					}
					catch (err) {
						logger.error(`Integration Website/API: ${data.apiMode} ${url} failed: ${(err as Error).message}`)
					}
				}
			}
		}
	}

	/**
	 * Run Send keys integration
	 * @param call Call object
	 * @param settings Test settings, used when testing integration
	 */
	async function integrationSendKeys(call?: PbxAppEmployeesExtensionCallWithDate, settings?: PbxAppSettingsDesktopIntegrationSendkeys) {
		const data = JSON.parse(JSON.stringify(settings ?? ss.settings.integration.sendkeys))

		if (call) {
			logger.info(`Integration Sendkeys: Invoked for call ${call._id} ${call.caller.number} -> ${call.callee.number} - state: ${call.state}`)
		}

		if (!enabled.value || !data.keys) { return }
		await sleep(data.delay ?? 0)

		const keys = await replace(data.keys, call, data.requireArgs ?? false, !!settings) // replaces {NUMBER}, {NUMBER-NO-PREFIX}, {NAME}, {VAR:cpr} in the keys
		let success = true

		if (keys) {
			data.keys = keys

			if (useIPC.value) {
				logger.info(`Integration: SendKeys invoked via IPC: keys=${data.keys} - focus=${data.focusProcess} - maximize=${data.maximizeProcess} - autoit=${data.useAutoit}`)

				try {
					success = await ipc.integrationSendKeys(data as IntegrationSendKeysData)
				}
				catch (err) {
					logger.error(`Integration SendKeys: IPC call failed: ${(err as Error).message}`)
					success = false
				}
			} else {
				try {
					logger.info(`Integration: SendKeys invoked via HTTP: keys=${data.keys} - focus=${data.focusProcess} - maximize=${data.maximizeProcess} - autoit=${data.useAutoit}`)
					const res = await http.post<{ success: boolean }>('/integration/sendkeys', data)

					success = res.data.success
				}
				catch (_) { success = false }
			}

			if (!success) {
				const { t } = useI18n()

				globalEvents.emit('CommonErrorModal', { header: t('integration.errorHeader'), message: t('integration.errorSendkeysMessage') })
			}
		}
	}

	/**
	 * Run Browser integration
	 * @param call Call object
	 * @param settings Test settings, used when testing integration
	 */
	async function integrationBrowser(call?: PbxAppEmployeesExtensionCallWithDate, settings?: PbxAppSettingsDesktopIntegrationBrowser) {
		const data = cloneObject(settings || ss.settings.integration.browser)
		let failed = false

		if (call) {
			logger.info(`Integration Browser: Invoked for call ${call._id} ${call.caller.number} -> ${call.callee.number} - state: ${call.state}`)
		}

		if (data.actions) {
			for (const element of data.actions) {
				if (element.action === 'insert') {
					const value = await replace(element.value as string, call, data.requireArgs ?? false, !!settings) // replaces {NUMBER}, {NUMBER-NO-PREFIX}, {NAME}, {VAR:cpr} in the keys
					if (value) {
						element.value = value
					} else {
						failed = true
					}
				}
			}
			if (!failed && enabled.value) {
				let success = true

				if (useIPC.value) {
					success = await ipc.integrationBrowser(data as unknown as IntegrationBrowserData)
				} else {
					try { await http.post('/integration/browser', data) }
					catch (_) { success = false }
				}

				if (!success) {
					const { t } = useI18n()

					globalEvents.emit('CommonErrorModal', { header: t('integration.errorHeader'), message: t('integration.errorBrowserMessage') })
				}
			}
		}
	}

	/**
	 * Handle call events that may trigger integrations
	 * @param event Event data
	 */
	async function callEvent(event: PbxEventCall) {
		// If a call is for my extension and I am receiving it and it is in state ringing or answered and it is not a local caller - then I want to know more...
		if (event.extension === employees.me?.primaryExtension?._id &&
			event.device === ss.realPhoneId &&
			event.leg === 'O' &&
			event.caller.length > 7 &&
			(event.event === PbxEventType.CALL_RING || event.event === PbxEventType.CALL_ANSWER)) {
			setTimeout(() => { // to allow employees to be updated visually first, we wait 10 ms
				// Find calls that concerns my device only
				const myCalls = employees.me?.primaryExtension?.calls.filter(call => call.device === ss.realPhoneId)

				// If I have exactly 1 incoming call in the right state on the phone that I am using - then we have a call that we need to care about
				if (myCalls?.length === 1) {
					const call = myCalls[0] as PbxAppEmployeesExtensionCallWithDate

					// Check each automation, if any is to be triggered then do it

					// Sendkeys
					if ((ss.settings.integration.sendkeys.when === PbxAppSettingsIntegrationRunWhen.RINGING && call.state === PbxCallState.CALL_RING) ||
						(ss.settings.integration.sendkeys.when === PbxAppSettingsIntegrationRunWhen.ANSWERED && call.state === PbxCallState.CALL_ANSWER)) {
						if (enabled.value) {
							integrationSendKeys(call).catch(() => { /** Ignore */ })
						}
					}

					// Browser
					if ((ss.settings.integration.browser.when === PbxAppSettingsIntegrationRunWhen.RINGING && call.state === PbxCallState.CALL_RING) ||
						(ss.settings.integration.browser.when === PbxAppSettingsIntegrationRunWhen.ANSWERED && call.state === PbxCallState.CALL_ANSWER)) {
						if (enabled.value) {
							integrationBrowser(call).catch(() => { /** Ignore */ })
						}
					}

					// Run
					if ((ss.settings.integration.run.when === PbxAppSettingsIntegrationRunWhen.RINGING && call.state === PbxCallState.CALL_RING) ||
						(ss.settings.integration.run.when === PbxAppSettingsIntegrationRunWhen.ANSWERED && call.state === PbxCallState.CALL_ANSWER)) {
						if (enabled.value) {
							integrationRun(call).catch(() => { /** Ignore */ })
						}
					}

					// Copy to clipboad
					if ((ss.settings.integration.copyToClipboard.when === PbxAppSettingsIntegrationRunWhen.RINGING && call.state === PbxCallState.CALL_RING) ||
						(ss.settings.integration.copyToClipboard.when === PbxAppSettingsIntegrationRunWhen.ANSWERED && call.state === PbxCallState.CALL_ANSWER)) {
						if (enabled.value) {
							integrationClipboard(call)
						}
					}

					// Website
					if ((ss.settings.integration.website.when === PbxAppSettingsIntegrationRunWhen.RINGING && call.state === PbxCallState.CALL_RING) ||
						(ss.settings.integration.website.when === PbxAppSettingsIntegrationRunWhen.ANSWERED && call.state === PbxCallState.CALL_ANSWER)) {
						integrationWebsite(call).catch(() => { /** Ignore */ })
						// Website works both in browser and app - thus no enabled check here
					}
				}
			}, 10)
		}

		if (event.extension === employees.me?.primaryExtension?._id && event.device === ss.realPhoneId && event.leg === 'O') {
			if (ss.settings.popupOnIncomingCall && event.event === PbxEventType.CALL_RING) {
				if (enabled.value) {
					if (useIPC.value) {
						ipc.show(true)
					}
				}
			} else if (ss.settings.popupRevertWhenDone && event.event === PbxEventType.CALL_END) {
				if (enabled.value) {
					if (useIPC.value) {
						ipc.hide(true)
					}
				}
			}
		}
	}

	// #endregion

	// Initialization

	realtime.pbxEvents.on('CALL_RING', callEvent)
	realtime.pbxEvents.on('CALL_ANSWER', callEvent)
	realtime.pbxEvents.on('CALL_END', callEvent)

	// #region Handle frontend events

	globalEvents.on('Auth:LoggedIn', () => {
		if (ss.settings.autoLaunchHide && started.getTime() + 15000 > new Date().getTime()) {
			hide()
		}
	})

	watch(() => ss.settings.alwaysOnTop, (newValue) => setAlwaysOnTop(newValue), { immediate: true })
	watch(() => ss.settings.zoom, (newValue) => setZoom(newValue), { immediate: true })
	watch(() => ss.settings.autoLaunch, (newValue) => setAutolaunch(newValue), { immediate: true })
	watch(() => ss.settings.window, (newValue) => {
		logger.info(`Electron: Window bounds changed to ${newValue.x},${newValue.y} ${newValue.width}x${newValue.height}`)
		setWindowBounds(newValue.x, newValue.y, newValue.width, newValue.height)
	}, { immediate: true })

	// #endregion

	// #region Handle backend events

	async function onAnswer() {
		const cr = employees.me?.currentRingingCall
		if (cr && ss.settings.softphoneId) {
			const session = sipClient.getSession(cr.callId, cr.caller.number, cr.state as unknown as SipClientTcxState)
			if (session) {
				sipClient.answer(session)
			}
		} else if (cr && ss.settings.phoneId) {
			try { await api.get(`/pbx/app/action/answer?device=${ss.settings.phoneId}&target=${cr._id}`) }
			catch (_) {
				const { t } = useI18n()

				globalEvents.emit('CommonErrorModal', { header: t('integration.myCallsHeader'), message: t('integration.myCallsFailedToAnswer') })
			}
		}
	}

	async function onHangup() {
		const cc = employees.me?.currentCall
		if (cc && ss.settings.softphoneId) {
			const session = sipClient.getSession(cc.callId, cc.caller.number, cc.state as unknown as SipClientTcxState)
			if (session) {
				sipClient.hangup(session)
			}
		} else if (cc && ss.settings.phoneId) {
			try { await api.get(`/pbx/app/action/hangup?target=${cc._id}`) }
			catch (_) {
				const { t } = useI18n()

				globalEvents.emit('CommonErrorModal', { header: t('integration.myCallsHeader'), message: t('integration.myCallsFailedHangup') })
			}
		}
	}

	function onSearch() {
		const el = document.getElementById('MainSearchInput') as HTMLInputElement

		if (el) {
			el.value = ''
			el.focus()
		}
	}

	async function onCall(number?: string) {
		if (number) {
			if (number.startsWith('00')) { number = '+' + number.substring(2) }
			if (!number.startsWith('+') && number.length > 7) { number = ss.pbxSettings?.nationalPrefix + number }

			if (!ss.realPhoneId) {
				const { t } = useI18n()

				globalEvents.emit('CommonErrorModal', { header: t('common.error.noPhoneHeader'), message: t('common.error.noPhoneMessage') })
				return
			}

			if (ss.settings.softphoneId) {
				sipClient.call(number)
			} else {
				try { await api.get(`/pbx/app/action/call?device=${ss.settings.phoneId}&target=${encodeURIComponent(number)}`) }
				catch (_) {
					const { t } = useI18n()

					globalEvents.emit('CommonErrorModal', { header: t('common.error.callFailedHeader'), message: t('common.error.callFailedMessage') })
				}
			}
		}
	}

	if (enabled.value) {
		realtime.configEvents.on('PBX_BEACON_CREATED', (event: ConfigEventBeacon) => {
			ss.beacons.push(event.data)
		})
		realtime.configEvents.on('PBX_BEACON_DELETED', (event: ConfigEventBeacon) => {
			ss.beacons = ss.beacons.filter(b => b._id !== event.id)
		})
		realtime.configEvents.on('PBX_BEACON_UPDATED', (event: ConfigEventBeacon) => {
			const index = ss.beacons.findIndex(b => b._id === event.id)
			if (index > -1) {
				ss.beacons[index] = event.data
			} else {
				ss.beacons.push(event.data)
			}
		})

		globalEvents.on('StatusRequest', () => {
			globalEvents.emit('StatusReport', { key: 'integration', value: useIPC.value ? true : pollApiConnected.value })
		})

		if (useIPC.value) {
			// Using modern IPC

			ipc.onAlive(() => {
				if (ss.isAuthenticated && ss.token) {
					if (ss.runtime.networkError || (ss.settings.softphoneId && !sipClient.registered) || !realtime.connected) {
						ipc.aliveReply(false)
					} else {
						ipc.aliveReply(true)
					}
				} else {
					ipc.aliveReply(true) // Login dialog is open - so we are in an OK state
				}
			})

			ipc.onCall(async number => {
				logger.debug(`Electron: Call event received: ${number}`)

				if (number) {
					if (number.startsWith('00')) { number = '+' + number.substring(2) }
					if (!number.startsWith('+') && number.length > 7) { number = ss.pbxSettings?.nationalPrefix + number }
					if (!ss.realPhoneId) {
						const { t } = useI18n()

						globalEvents.emit('CommonErrorModal', { header: t('common.error.noPhoneHeader'), message: t('common.error.noPhoneMessage') })
						return
					}
					if (ss.settings.softphoneId) {
						sipClient.call(number)
					} else {
						try { await api.get(`/pbx/app/action/call?device=${ss.settings.phoneId}&target=${encodeURIComponent(number)}`) }
						catch (_) {
							const { t } = useI18n()

							globalEvents.emit('CommonErrorModal', { header: t('common.error.callFailedHeader'), message: t('common.error.callFailedMessage') })
						}
					}
				}
			})

			ipc.onError((header, message) => {
				logger.debug(`Electron: Error event received: ${message}`)

				globalEvents.emit('CommonErrorModal', { header, message })
			})

			ipc.onFullscreen(fullscreen => {
				logger.debug(`Electron: Fullscreen event received: fullscreen is ${fullscreen}`)

				ss.settings.fullscreen = fullscreen
			})

			ipc.onVisibility(visible => {
				logger.debug(`Electron: VisibilityChange event received: visible is ${visible}`)

				ss.runtime.visible = visible
			})

			ipc.onGlobalShortcut(async (command, number) => {
				logger.debug(`Electron: Global shortcut event received: ${command}`)

				switch (command) {
					case 'ANSWER': onAnswer(); break
					case 'HANGUP': onHangup(); break
					case 'SEARCH': onSearch(); break
					case 'CALL': onCall(number); break
				}
			})

			ipc.onLog((severity, message) => {
				switch (severity) {
					case 'INFO': logger.info(message); break
					case 'WARNING': logger.warning(message); break
					case 'ERROR': logger.error(message); break
					case 'DEBUG': logger.debug(message); break
				}
			})

			ipc.onWindowBounds((x, y, width, height) => {
				logger.debug(`Electron: WindowBounds event received: ${x}/${y} - ${width}x${height}`)

				if (ss.settings.window.x !== x) { ss.settings.window.x = x }
				if (ss.settings.window.y !== y) { ss.settings.window.y = y }
				if (ss.settings.window.width !== width) { ss.settings.window.width = width }
				if (ss.settings.window.height !== height) { ss.settings.window.height = height }
			})

			ipc.onLock(async (locked) => {
				logger.debug(`Electron: Lock event received - desktop is now: ${locked ? 'locked' : 'unlocked'}`)
				ss.runtime.desktopLocked = locked
				kuando.instance.save() // Save will update the current state of the busylight

				if (!ss.settings.dndWhenDesktopLocked) { return }
				if (employees.me?.extensionSettings) {
					try {
						const res = await api.get<PbxExtensionDndResponse>(`/pbx/extension/${employees.me.extensionSettings._id}/dnd?sip=${ss.realPhoneId}&state=${locked}`)

						if (!res.data.dnd === locked) {
							const { t } = useI18n()

							globalEvents.emit('CommonErrorModal', { header: t('settings.dnd.updateFailedHeader'), message: t('settings.dnd.updateFailedMessage') })
						}
					}
					catch (_) {
						const { t } = useI18n()

						globalEvents.emit('CommonErrorModal', { header: t('settings.dnd.updateFailedHeader'), message: t('settings.dnd.updateFailedMessage') })
					}
				}
			})

			ipc.onBeacon(async (event: BeaconEvent, beacon?: Beacon) => {
				lastBeaconEvent.value = new Date()

				switch (event) {
					case BeaconEvent.ENTERED:
						if (ss.isInDebugMode && beacon) {
							logger.debug(`Electron: Beacon event received: ${event} - ${beacon.name} - ${beacon.uuid} - ${beacon.major}:${beacon.minor}`)
						}

						if (beacon) {
							const beaconSettings = ss.beacons.find(b => b.uuid.toUpperCase() === beacon.uuid.toUpperCase() && b.major === beacon.major && b.minor === beacon.minor)

							if (beaconSettings && beaconSettings.battery != beacon.battery) {
								try {
									await api.post(`/pbx/beacon/${beaconSettings._id}/battery`, { battery: beacon.battery })
								}
								catch (err) {
									logger.error(`Electron: Failed to update battery level for beacon ${beaconSettings._id} (${beacon.name}): ${(err as Error).message}`)
								}
							}
						}
						break
					case BeaconEvent.LEFT:
						if (ss.isInDebugMode && beacon) {
							logger.debug(`Electron: Beacon event received: ${event} - ${beacon.name} - ${beacon.uuid} - ${beacon.major}:${beacon.minor}`)
						}
						break
					case BeaconEvent.CURRENT:
						if (beacon) {
							if (ss.isInDebugMode) { logger.debug(`Electron: Beacon event received: ${event} - ${beacon.name} - ${beacon.uuid} - ${beacon.major}:${beacon.minor}`) }
							if (!currentBeacon.value || (currentBeacon.value.uuid !== beacon.uuid || currentBeacon.value.major !== beacon.major || currentBeacon.value.minor !== beacon.minor)) {
								currentBeacon.value = beacon

								const beaconSettings = ss.beacons.find(b => b.uuid.toUpperCase() === beacon.uuid.toUpperCase() && b.major === beacon.major && b.minor === beacon.minor)
								if (beaconSettings && ss.settings.beaconsMayUpdateLocation) {
									try {
										await api.post(`/employee/${ss.auth?.employee}`, { presence: { location: beaconSettings.location } })
										logger.info(`Electron: User changed location to: ${beaconSettings.location}`)
									}
									catch (err) {
										logger.error(`Electron: Failed to update user location: ${(err as Error).message}`)
									}
								} else {
									logger.debug(`Electron: Beacon event received: ${event} - ${beacon.uuid} - ${beacon.major}:${beacon.minor} - but no location match`)
								}
							} else {
								logger.debug(`Electron: Beacon event received: ${event} - no change`)
							}
						} else if (currentBeacon.value) {
							currentBeacon.value = undefined
							if (ss.isInDebugMode) { logger.debug(`Electron: Beacon event received: ${event} - no beacons in range`) }
							if (ss.settings.beaconsMayUpdateLocation) {
								try {
									await api.post(`/employee/${ss.auth?.employee}`, { presence: { location: '' } })
									logger.info('Electron: User changed location to: <blank>')
								}
								catch (err) {
									logger.error(`Electron: Failed to update user location: ${(err as Error).message}`)
								}
							}
						} else {
							logger.debug(`Electron: Beacon event received: ${event} - no change 2`)
						}
						break
					default:
						break
				}
			})
		} else {
			// Using old style polling
			pollLocalApi()
		}
	}

	// #endregion

	return { enabled, useIPC, connected, setAlwaysOnTop, setZoom, setAutolaunch, setAppName, hide, show, ping, download, setFullscreen, setGlobalShortcuts, openExternal,
		terminate, zerotier, devTools, integrationSendKeys, integrationBrowser, integrationRun, integrationClipboard, integrationWebsite, notificationClose, notificationShow,
		setBeaconScanner, isReceivingBeaconEvents }
})
